import { jsPDF } from "jspdf";

import { formatRetailCtmPdfDateTime } from "./formatters";
import type { RetailCtmPdfIconName, RetailCtmPdfImage } from "./icons";
import type {
  RetailCtmSummaryPdfData,
  RetailCtmSummaryPdfField,
} from "./types";

const PAGE = { width: 210, height: 297 } as const;
const MARGIN = 7;
const CONTENT_WIDTH = PAGE.width - MARGIN * 2;
const COLUMN_GAP = 6;
const COLUMN_WIDTH = (CONTENT_WIDTH - COLUMN_GAP) / 2;
const ROW_HEIGHT = 4.6;
const BOX_PADDING = 3;
const FIELD_LINE_HEIGHT_FACTOR = 1.05;
const BOX_RADIUS = 2;
const SECTION_GAP = 8;
const GAP_AFTER_VALIDATION = 4;
const GAP_BEFORE_VEHICLE_SECTION = 3;
const SECTION_TITLE_GAP = 4;
const ICON_SIZE = 4.5;
const FIELD_FONT = 9;
const TITLE_FONT = 13;
const TITLE_LINE_HEIGHT = 5.5;
const VALIDATION_FONT = 8;
const SECTION_TITLE_FONT = 10.5;
const KVPS_CAPTION_FONT = 10;
const KVPS_VALUE_FONT = 12;
const KVPS_ZONE_MIN_HEIGHT = 20;
const HEADER_CARD_HEIGHT = 20;
const HEADER_BRAND_LOGO_RIGHT_INSET = 8;
const GAP_AFTER_HEADER_CARD = 4;

const COLORS = {
  label: [33, 95, 154] as [number, number, number],
  panel: [220, 234, 247] as [number, number, number],
  caption: [110, 110, 110] as [number, number, number],
  black: [0, 0, 0] as [number, number, number],
};

type SectionColumns =
  | { mode: "single"; fields: RetailCtmSummaryPdfField[] }
  | {
      mode: "double";
      left: RetailCtmSummaryPdfField[];
      right: RetailCtmSummaryPdfField[];
    };

function fitImage(
  widthPx: number,
  heightPx: number,
  maxWidth: number,
  maxHeight: number,
) {
  const ratio = widthPx / heightPx;
  let width = maxWidth;
  let height = width / ratio;
  if (height > maxHeight) {
    height = maxHeight;
    width = height * ratio;
  }
  return { width, height };
}

export class RetailCtmSummaryPdfDocument {
  private readonly pdf: jsPDF;
  private readonly icons: Record<RetailCtmPdfIconName, RetailCtmPdfImage>;
  private y = MARGIN;

  constructor(icons: Record<RetailCtmPdfIconName, RetailCtmPdfImage>) {
    this.icons = icons;
    this.pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  }

  render(
    data: RetailCtmSummaryPdfData,
    brandLogo?: RetailCtmPdfImage,
    generatedAt: Date = new Date(),
  ): jsPDF {
    this.drawHeaderCard(brandLogo);
    this.drawValidationLine(generatedAt);
    this.drawKvpsContext(data.kvpsIdentifier, data.kvpsLabel);
    this.drawInformationSection(
      "Informations Véhicule",
      "car",
      this.buildVehicleSection(data),
      {
        topGap: GAP_BEFORE_VEHICLE_SECTION,
      },
    );
    this.drawClientSection(data);
    this.drawInformationSection("Financement", "creditCard", {
      mode: "double",
      left: [
        { label: "Location véhicule", value: data.funding.isLeased },
        { label: "Location VWFS", value: data.funding.isVwfsLeasing },
        { label: "Type de location", value: data.funding.leasingType },
        { label: "N° de SIRET loueur", value: data.funding.lessorSiret },
        { label: "N° de BDC loueur", value: data.funding.lessorBdcNumber },
      ],
      right: [
        { label: "Type de vente", value: data.funding.saleChannel },
        { label: "Reprise du véhicule", value: data.funding.hasTradeIn },
      ],
    });
    this.drawInformationSection(
      "Informations Complémentaires",
      "fileText",
      this.buildAdditionalSection(data),
    );
    return this.pdf;
  }

  private buildAdditionalSection(
    data: RetailCtmSummaryPdfData,
  ): SectionColumns {
    const fields: RetailCtmSummaryPdfField[] = [
      { label: "Vendeur", value: data.additional.salesPerson },
      { label: "Consentement", value: data.additional.consent },
      {
        label: "Date du bon de commande",
        value: data.additional.signatureDate,
      },
      {
        label: "Date de livraison souhaitée",
        value: data.additional.preferredDeliveryDate,
      },
      {
        label: "Date extrême de livraison",
        value: data.additional.maxDeliveryDate,
      },
      {
        label: "Semaine de production souhaitée",
        value: data.additional.preferredProductionWeek,
      },
      {
        label: "Reprises véhicules",
        value: data.additional.tradeInLines.join("\n"),
      },
    ];

    return { mode: "single", fields };
  }

  private buildVehicleSection(data: RetailCtmSummaryPdfData): SectionColumns {
    const rightFields: RetailCtmSummaryPdfField[] = [
      { label: "Année de commande", value: data.vehicle.commissionYear },
      { label: "Statut de commande", value: data.vehicle.orderStatus },
      {
        label: "N° de commande usine",
        value: data.vehicle.commissionNumber,
      },
      { label: "N° de châssis", value: data.vehicle.vin },
      {
        label: "Véhicule carrossé",
        value: data.vehicle.isBodyworkVehicle,
      },
    ];

    if (data.vehicle.showBodyworkDetails) {
      rightFields.push(
        { label: "Type de carrosserie", value: data.vehicle.bodyTypeLabel },
        { label: "Nom du carrossier", value: data.vehicle.bodyShopName },
        { label: "KVPS du carrossier", value: data.vehicle.bodyShopKvps },
        {
          label: "Véhicule expédié chez le carrossier",
          value: data.vehicle.shippedToBodyShop,
        },
      );
    }

    return {
      mode: "double",
      left: [
        { label: "Marque", value: data.vehicle.brand },
        { label: "Modèle", value: data.vehicle.model },
        { label: "Peinture", value: data.vehicle.paint },
        { label: "Intérieur", value: data.vehicle.interior },
        { label: "Options", value: data.vehicle.options },
      ],
      right: rightFields,
    };
  }

  private ensureSpace(height: number) {
    if (this.y + height > PAGE.height - MARGIN) {
      this.pdf.addPage();
      this.y = MARGIN;
    }
  }

  private drawImage(
    image: RetailCtmPdfImage,
    x: number,
    y: number,
    width: number,
    height: number,
  ) {
    this.pdf.addImage(image.dataUrl, "PNG", x, y, width, height);
  }

  private drawIcon(
    name: RetailCtmPdfIconName,
    x: number,
    y: number,
    sizeMm = ICON_SIZE,
  ) {
    this.drawImage(this.icons[name], x, y, sizeMm, sizeMm);
  }

  private measureField(field: RetailCtmSummaryPdfField, width: number): number {
    this.pdf.setFontSize(FIELD_FONT);
    this.pdf.setFont("helvetica", "bold");
    const prefix = `${field.label} : `;
    const valueWidth = Math.max(width - this.pdf.getTextWidth(prefix), 10);
    const lines = this.pdf.splitTextToSize(field.value, valueWidth);
    return Math.max(lines.length, 1) * ROW_HEIGHT;
  }

  private drawField(
    field: RetailCtmSummaryPdfField,
    x: number,
    width: number,
    y: number,
  ): number {
    this.pdf.setFontSize(FIELD_FONT);
    this.pdf.setFont("helvetica", "bold");

    const prefix = `${field.label} : `;
    const prefixWidth = this.pdf.getTextWidth(prefix);
    const valueX = x + prefixWidth;
    const valueWidth = Math.max(x + width - valueX, 10);

    this.pdf.setTextColor(...COLORS.label);
    this.pdf.text(prefix, x, y);

    const valueLines = this.pdf.splitTextToSize(field.value, valueWidth);
    this.pdf.setTextColor(...COLORS.black);
    this.pdf.setLineHeightFactor(FIELD_LINE_HEIGHT_FACTOR);
    this.pdf.text(valueLines, valueX, y);

    if (field.underlineValue) {
      const underlineY = y + 0.7;
      for (const line of valueLines) {
        const lineWidth = this.pdf.getTextWidth(line);
        this.pdf.setDrawColor(...COLORS.label);
        this.pdf.setLineWidth(0.2);
        this.pdf.line(valueX, underlineY, valueX + lineWidth, underlineY);
      }
    }

    return Math.max(valueLines.length, 1) * ROW_HEIGHT;
  }

  private drawHeaderCard(brandLogo?: RetailCtmPdfImage) {
    const top = this.y;
    const height = HEADER_CARD_HEIGHT;

    this.pdf.setFillColor(...COLORS.panel);
    this.pdf.roundedRect(
      MARGIN,
      top,
      CONTENT_WIDTH,
      height,
      BOX_RADIUS,
      BOX_RADIUS,
      "F",
    );

    if (brandLogo) {
      const logo = fitImage(brandLogo.width, brandLogo.height, 16, 16);
      this.drawImage(
        brandLogo,
        PAGE.width - MARGIN - logo.width - HEADER_BRAND_LOGO_RIGHT_INSET,
        top + (height - logo.height) / 2,
        logo.width,
        logo.height,
      );
    }

    this.pdf.setFont("helvetica", "bold");
    this.pdf.setFontSize(TITLE_FONT);
    this.pdf.setTextColor(...COLORS.black);

    const centerX = PAGE.width / 2;
    const titleBlockHeight = TITLE_LINE_HEIGHT * 2;
    const titleY = top + (height - titleBlockHeight) / 2 + TITLE_LINE_HEIGHT;

    this.pdf.text("Résumé des informations de votre", centerX, titleY, {
      align: "center",
    });
    this.pdf.text("contremarque client", centerX, titleY + TITLE_LINE_HEIGHT, {
      align: "center",
    });

    this.y = top + height + GAP_AFTER_HEADER_CARD;
  }

  private drawValidationLine(generatedAt: Date) {
    const validationLabel = `Validation contremarque le : ${formatRetailCtmPdfDateTime(generatedAt)}`;

    this.ensureSpace(6);
    this.pdf.setFont("helvetica", "normal");
    this.pdf.setFontSize(VALIDATION_FONT);
    this.pdf.setTextColor(...COLORS.caption);
    this.pdf.text(validationLabel, PAGE.width / 2, this.y + 2, {
      align: "center",
    });
    this.y += 4 + GAP_AFTER_VALIDATION;
  }

  private drawKvpsContext(identifier: string, label?: string | null) {
    if (!identifier.trim()) return;

    const value = label?.trim()
      ? `${identifier.trim()} – ${label.trim()}`
      : identifier.trim();

    const iconX = MARGIN;
    const textX = iconX + ICON_SIZE + 2;
    const textWidth = CONTENT_WIDTH - ICON_SIZE - 2;

    this.pdf.setFont("helvetica", "bold");
    this.pdf.setFontSize(KVPS_VALUE_FONT);
    const valueLines = this.pdf.splitTextToSize(value, textWidth);
    const captionLineHeight = 5;
    const valueLineHeight = 6.2;
    const textBlockHeight =
      captionLineHeight + 1.5 + valueLines.length * valueLineHeight;
    const groupHeight = Math.max(ICON_SIZE, textBlockHeight);
    const zoneHeight = Math.max(KVPS_ZONE_MIN_HEIGHT, groupHeight + 1);

    this.ensureSpace(zoneHeight + SECTION_GAP);

    const groupTop = this.y + 1;
    const iconY = groupTop + (groupHeight - ICON_SIZE) / 2;
    const textTop = groupTop + (groupHeight - textBlockHeight) / 2;
    const captionY = textTop + captionLineHeight;

    this.drawIcon("building", iconX, iconY, ICON_SIZE);

    this.pdf.setFont("helvetica", "normal");
    this.pdf.setFontSize(KVPS_CAPTION_FONT);
    this.pdf.setTextColor(...COLORS.caption);
    this.pdf.text("Contexte KVPS", textX, captionY);

    this.pdf.setFont("helvetica", "bold");
    this.pdf.setFontSize(KVPS_VALUE_FONT);
    this.pdf.setTextColor(...COLORS.black);
    this.pdf.text(valueLines, textX, captionY + 6);

    this.y += zoneHeight + GAP_BEFORE_VEHICLE_SECTION;
  }

  private drawSectionHeading(
    title: string,
    icon: RetailCtmPdfIconName,
    topGap = SECTION_GAP,
  ) {
    this.y += topGap;
    this.ensureSpace(12);
    this.drawIcon(icon, MARGIN, this.y - 3.5);
    this.pdf.setFont("helvetica", "bold");
    this.pdf.setFontSize(SECTION_TITLE_FONT);
    this.pdf.setTextColor(...COLORS.black);
    this.pdf.text(title, MARGIN + ICON_SIZE + 2, this.y);
    this.y += SECTION_TITLE_GAP;
  }

  private drawColoredFieldsPanel(columns: SectionColumns) {
    const leftX = MARGIN + BOX_PADDING;
    const rightX = MARGIN + COLUMN_WIDTH + COLUMN_GAP + BOX_PADDING;
    const innerWidth = COLUMN_WIDTH - BOX_PADDING * 2;
    const fullWidth = CONTENT_WIDTH - BOX_PADDING * 2;

    const leftFields =
      columns.mode === "double" ? columns.left : columns.fields;
    const rightFields = columns.mode === "double" ? columns.right : [];
    const rowCount = Math.max(leftFields.length, rightFields.length, 1);

    let contentHeight = 0;
    for (let i = 0; i < rowCount; i += 1) {
      const left = leftFields[i]
        ? this.measureField(
            leftFields[i],
            columns.mode === "double" ? innerWidth : fullWidth,
          )
        : 0;
      const right = rightFields[i]
        ? this.measureField(rightFields[i], innerWidth)
        : 0;
      contentHeight += Math.max(left, right, ROW_HEIGHT);
    }

    const boxHeight = contentHeight + BOX_PADDING * 2;
    this.ensureSpace(boxHeight + 4);

    this.pdf.setFillColor(...COLORS.panel);
    this.pdf.roundedRect(
      MARGIN,
      this.y,
      CONTENT_WIDTH,
      boxHeight,
      BOX_RADIUS,
      BOX_RADIUS,
      "F",
    );

    let rowY = this.y + BOX_PADDING + 3;
    for (let i = 0; i < rowCount; i += 1) {
      const leftField = leftFields[i];
      const rightField = rightFields[i];
      const leftHeight = leftField
        ? this.drawField(
            leftField,
            leftX,
            columns.mode === "double" ? innerWidth : fullWidth,
            rowY,
          )
        : 0;
      const rightHeight = rightField
        ? this.drawField(rightField, rightX, innerWidth, rowY)
        : 0;
      rowY += Math.max(leftHeight, rightHeight, ROW_HEIGHT);
    }

    this.y += boxHeight + 4;
  }

  private drawInformationSection(
    title: string,
    icon: RetailCtmPdfIconName,
    columns: SectionColumns,
    options?: { topGap?: number },
  ) {
    this.drawSectionHeading(title, icon, options?.topGap);
    this.drawColoredFieldsPanel(columns);
  }

  private drawClientSection(data: RetailCtmSummaryPdfData) {
    if (data.client.isRentSale) {
      this.drawInformationSection("Informations Client", "user", {
        mode: "single",
        fields: [
          {
            label: "Canal",
            value:
              "RENT — les informations client ne sont pas renseignées à cette étape.",
          },
        ],
      });
      return;
    }

    if (data.client.isDirectorSale) {
      this.drawInformationSection("Informations Client", "user", {
        mode: "double",
        left: [
          { label: "Type de client", value: data.client.clientType },
          { label: "N° de SIRET", value: data.client.companySiret },
          {
            label: "Dénomination commerciale",
            value: data.client.companyDenomination,
          },
          { label: "Nom", value: data.client.lastName },
          { label: "Prénom", value: data.client.firstName },
          { label: "Adresse", value: data.client.address },
          { label: "Code postal", value: data.client.postalCode },
          { label: "Ville", value: data.client.city },
        ],
        right: [
          {
            label: "Email",
            value: data.client.email,
            underlineValue: data.client.email.length > 0,
          },
          { label: "Téléphone mobile", value: data.client.mobilePhone },
        ],
      });
      return;
    }

    this.drawInformationSection("Informations Client", "user", {
      mode: "double",
      left: [
        { label: "Type de client", value: data.client.clientType },
        { label: "Nom", value: data.client.lastName },
        { label: "Prénom", value: data.client.firstName },
        { label: "Adresse", value: data.client.address },
        { label: "Code postal", value: data.client.postalCode },
        { label: "Ville", value: data.client.city },
      ],
      right: [
        {
          label: "Email",
          value: data.client.email,
          underlineValue: data.client.email.length > 0,
        },
        { label: "Téléphone mobile", value: data.client.mobilePhone },
        { label: "Dirigeant société", value: data.client.directorSale },
      ],
    });
  }
}
